import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    List,
    ListItemIcon,
    ListItemText,
    ListItemButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { SttList } from './SttList';
import { AppContext } from '../AppContext';
import { useCrud } from '../../lib/hooks';
import { SttFormText } from '../forms/SttFormText';

type Tag = {
    id: number;
    name: string;
};

type Activity = {
    id: number;
    tags?: Tag[];
};

type TagsResponse = {
    rows: Tag[];
};

// Unused type - kept for reference
// type _ActivityResponse = {
//     activity?: {
//         tags?: Tag[];
//     };
// };

type Props = {
    activity: Activity;
    onUpdateActivityTags: (tags?: Tag[]) => void;
};

export default function SttTagsList({
    activity,
    onUpdateActivityTags,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [openAddingTag, setOpenAddingTag] = useState(false);
    const [, , , Get, Post, , Delete] = useCrud<any>();
    const [scTags, setScTags] = useState<TagsResponse>();
    const [tags, setTags] = useState<Tag[] | undefined>();
    const [newTag, setNewTag] = useState('');

    const getSportCenterTags = () => {
        if (cxt?.sc?.id) {
            Get(`/scs/${cxt.sc?.id}/tags`).then((response: any) =>
                setScTags(response),
            );
        }
    };

    const addSportCenterTag = () => {
        if (cxt?.sc?.id) {
            Post(`/scs/tags`, { name: newTag, description: newTag }).then(
                () => {
                    getSportCenterTags();
                },
            );
        }
    };

    const getActivityTags = () => {
        if (cxt?.sc?.id && activity) {
            Get(`/activities/${activity.id}`, { trash: true }).then(
                (response: any) => {
                    setTags(response?.activity?.tags);
                    onUpdateActivityTags(response?.activity?.tags);
                },
            );
        }
    };

    const addActivityTag = (tagId: number) => {
        Post(`/scs/activities/${activity.id}/tags/${tagId}`).then(() => {
            getActivityTags();
        });
    };

    const deleteActivityTag = (tagId: number) => {
        Delete(`/scs/activities/${activity.id}/tags/${tagId}`).then(() => {
            getActivityTags();
        });
    };

    useEffect(() => {
        if (openAddingTag) getSportCenterTags();
    }, [openAddingTag]);

    useEffect(() => {
        if (activity) setTags(activity?.tags);
    }, [activity]);

    return (
        <>
            <SttList
                title={t('Tags')}
                onAdd={() => setOpenAddingTag(true)}
                onDelete={(tag: Tag) => deleteActivityTag(tag.id)}
                data={tags?.map((tag: any) => ({
                    caption: (
                        <Box display="flex">
                            <Typography>{tag.name}</Typography>
                        </Box>
                    ),
                    objToSelect: tag,
                }))}
            />

            <Dialog
                onClose={() => setOpenAddingTag(false)}
                open={openAddingTag}
            >
                <DialogTitle>{t('Tags')}</DialogTitle>
                <Box pl={3} pr={3}>
                    <Grid container>
                        <Grid size={{ xs: 10 }}>
                            <SttFormText
                                name="newTag"
                                caption={t('NewTag')}
                                defVal={newTag}
                                onChange={(tag: any) => setNewTag(tag.value)}
                            />
                        </Grid>

                        <Grid size={{ xs: 2 }}>
                            <IconButton
                                disabled={!newTag}
                                onClick={() => addSportCenterTag()}
                            >
                                <AddIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <List>
                        {scTags?.rows.map((scTag, idx) => (
                            <ListItemButton
                                key={idx}
                                onClick={() => addActivityTag(scTag.id)}
                            >
                                <ListItemIcon>
                                    <LocalOfferIcon />
                                </ListItemIcon>
                                <ListItemText primary={scTag.name} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Dialog>
        </>
    );
}
